import { cva, type VariantProps } from 'class-variance-authority'
import { InfoIcon } from 'lucide-react'
import { cn } from '@/src/utils/styles'

const badgeVariants = cva(
  'inline-flex items-center justify-center gap-0.5 flex-nowrap rounded-full bg-surface2 text-primary',
  {
    variants: {
      variant: {
        default: 'bg-surface2 text-primary',
        success: 'bg-success text-success-text',
        muted: 'bg-surface2 text-secondary',
        loud: 'light bg-background text-primary',
        notification: 'bg-orange-600 text-gray-50',
      },
      size: {
        small: 'px-0.5 min-h-4 min-w-4 text-center text-xs',
        default: 'px-1 min-h-6 min-w-6 text-center text-xs',
        large: 'px-1.5 min-h-8 min-w-8 text-center text-base',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

const badgeContentVariants = cva('', {
  variants: {
    size: {
      small: 'px-0.5',
      default: 'px-1',
      large: 'px-1',
    },
    defaultVariants: {
      size: 'default',
    },
  },
})

const badgeIconVariants = {
  variants: {
    size: {
      small: 12,
      default: 16,
      large: 20,
    },
  },
  defaultVariants: {
    size: 'default',
  },
}

interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {
  iconClassName?: string
  showLeadingIcon?: boolean
  showTrailingIcon?: boolean
  leftIcon?: React.ReactElement
  rightIcon?: React.ReactElement
}

function Badge({
  className,
  children,
  showLeadingIcon,
  showTrailingIcon,
  leftIcon,
  rightIcon,
  variant,
  size,
  iconClassName,
  ...props
}: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant, size }), className)} {...props}>
      {leftIcon && leftIcon}
      {showLeadingIcon && (
        <InfoIcon
          className={cn('text-tertiary', iconClassName)}
          size={badgeIconVariants.variants.size[size ?? 'default']}
        />
      )}
      {children && (
        <span className={cn('px-1', badgeContentVariants({ size }))}>
          {children}
        </span>
      )}
      {showTrailingIcon && (
        <InfoIcon
          className={cn('text-tertiary', iconClassName)}
          size={badgeIconVariants.variants.size[size ?? 'default']}
        />
      )}
      {rightIcon && rightIcon}
    </div>
  )
}

export { Badge }
